
























import { Component, Prop, Vue } from 'vue-property-decorator';
import SearchResultStore from '@/stores/search/searchResult.store';
import { getModule } from 'vuex-module-decorators';
import {
  PageMetadataDto,
  PageRequestDto,
  ProductFacetsDto,
  ProductSearchFilterDto,
  ProductSearchRequestDto,
} from '@/api';
import ProductGrid from '@/views/product/ProductGrid.vue';
import CategoryFilter from '@/views/search/filter/CategoryFilter.vue';
import LoadingIndicator from '@/views/common/LoadingIndicator.vue';
import { SortSetting, SortUtils } from '@/util/SortUtils';
import SortSelection from '@/views/search/filter/SortSelection.vue';
import SearchFilterControl from '@/views/search/filter/SearchFilterControl.vue';
import ShowMorePagination from '@/views/common/ShowMorePagination.vue';
import { SearchUtility } from '@/stores/search/util';

@Component({
  components: {
    ShowMorePagination,
    SearchFilterControl,
    LoadingIndicator,
    CategoryFilter,
    ProductGrid,
    SortSelection,
  },
})
export default class SearchResult extends Vue {
  private loadingNewResult = false;
  private loadingNewPage = false;
  private searchResultStore = getModule(SearchResultStore, this.$store);
  private currentSortSettingId = '0';

  @Prop({required: true})
  public query!: string;

  public async mounted() {
    if (this.query && !this.$route.meta.isBack) {
      const searchRequest = this.searchResultStore.currentSearchRequest;
      searchRequest.queryString = this.query;
      await this.pushRequest(searchRequest);
    }
  }

  get pageRequest(): PageRequestDto {
    return this.searchResultStore.currentSearchRequest.page ?? SearchUtility.EmptyPageRequest;
  }

  get sortSetting() {
    return this.currentSortSettingId;
  }

  get products() {
    const products = this.searchResultStore.currentProducts;
    return products.length === 0 ? null : products;
  }

  get searchFilter(): ProductSearchFilterDto {
    return this.searchResultStore.currentSearchRequest.filter || {};
  }

  get searchQueryString(): string {
    return this.searchResultStore.currentSearchRequest.queryString;
  }

  get resultFacets(): ProductFacetsDto {
    return this.searchResultStore.currentSearchResult.facets;
  }

  get sortSettings(): SortSetting[] {
    return SortUtils.getSearchSortSettings();
  }

  get currentPageMeta(): PageMetadataDto | null {
    return this.searchResultStore.currentSearchResult?.page ?? SearchUtility.EmptyPageResult;
  }

  get distinctProductCount(): number {
    return this.searchResultStore.currentDistinctProductCount;
  }

  public async fetchAdditionalPage(page: PageMetadataDto) {
    this.loadingNewPage = true;
    try {
      await this.searchResultStore.fetchAdditionalPage(page);
    } finally {
      this.loadingNewPage = false;
    }
  }

  public updateSortSetting(newSortSettingId: string) {
    this.currentSortSettingId = newSortSettingId;
    const newSetting = this.sortSettings.find((s) => s.id === newSortSettingId);
    this.searchResultStore.updateSorting(newSetting ? newSetting : {fieldName: undefined, direction: undefined});
  }

  get category(): string {
    return this.searchResultStore.currentSearchRequest.filter &&
      this.searchResultStore.currentSearchRequest.filter.categoryId || '';
  }

  set category(category: string) {
    this.setCategoryFilter(category);
  }

  get parentCategoryFacets() {
    return this.searchResultStore.currentSearchResult.facets.parentCategoryFacets || [];
  }

  get childCategoryFacets() {
    return this.searchResultStore.currentSearchResult.facets.childCategoryFacets || [];
  }

  public async setCategoryFilter(categoryId: string) {
    const searchFilter = this.searchFilter;
    searchFilter.categoryId = categoryId;
    await this.filterSearch(searchFilter);
  }

  public async filterSearch(searchFilter: ProductSearchFilterDto) {
    const searchRequest = this.searchResultStore.currentSearchRequest;
    searchRequest.filter = searchFilter;
    await this.pushRequest(searchRequest);
  }

  private async pushRequest(searchRequest: ProductSearchRequestDto) {
    this.loadingNewResult = true;
    try {
      await this.searchResultStore.updateSearchRequest(searchRequest);
    } finally {
      this.loadingNewResult = false;
    }
  }
}
